import { Button, ButtonType, UiComponentSize } from '@altibox/design-system-component-lib';
import React, { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { unstable_usePrompt as usePrompt } from 'react-router-dom';
import { useLocation } from 'react-router';

import { ProductInformationStep } from 'app/components/information-step';
import { i18n } from 'app/i18n';
import { RequestType } from 'app/pages/order/redux/model/order-request';
import { selectOrderRequests, selectShoppingCartOrThrow } from 'app/pages/order/redux/order-selector';
import { OrderRequestsUtils } from 'app/pages/order/utils/order-requests-utils';
import { emailImageProps, heartImageProps } from 'app/resources/images';
import { NavigationService, navigationService } from 'app/services/navigation-service';
import { useLeaveWhileLoading } from 'app/pages/order/hooks/use-leave-while-loading';

import { DeliveryInformation } from './delivery-information';
import { EquipmentInformation } from './equipment-information';
import { SuccessInformation } from './success-information/success-information';
import { OrderDetails } from './order-details';
import { StyledButtonContainer, StyledContainer, StyledHeading, StyledSuccessItems } from './success-page.styled';

export const SuccessPage: FunctionComponent = () => {
    const shoppingCart = useSelector(selectShoppingCartOrThrow);
    const { homeGatewayUpgrade } = shoppingCart;
    const loading = OrderRequestsUtils.isRequestPending(
        useSelector(selectOrderRequests),
        RequestType.SUBMIT_SHOPPING_CART,
    );
    const location = useLocation();

    useLeaveWhileLoading(loading);

    usePrompt({
        when:
            loading && ![NavigationService.ERROR, NavigationService.ERROR_SESSION_EXPIRED].includes(location.pathname),
        message: i18n.prompt.leavingPageMessage,
    });

    const handleGoToMyPages = (): void => {
        navigationService.goToMyPages();
    };

    return (
        <StyledContainer>
            <StyledHeading component="h1" variant="headline2">
                {loading ? i18n.page.success.loading.title : i18n.page.success.title}
            </StyledHeading>
            <StyledSuccessItems>
                <ProductInformationStep icon={emailImageProps} loading={loading}>
                    <OrderDetails />
                </ProductInformationStep>
                <DeliveryInformation loading={loading} isHomeGatewayUpgradeNeeded={!!homeGatewayUpgrade} />
                <EquipmentInformation loading={loading} isHomeGatewayUpgradeNeeded={!!homeGatewayUpgrade} />
                <SuccessInformation loading={loading} title={i18n.page.success.enjoy.title} icon={heartImageProps} />
            </StyledSuccessItems>
            {!loading && (
                <StyledButtonContainer>
                    <Button
                        buttonType={ButtonType.PRIMARY_A}
                        uiComponentSize={UiComponentSize.LARGE}
                        onClick={handleGoToMyPages}
                    >
                        {i18n.page.success.button}
                    </Button>
                </StyledButtonContainer>
            )}
        </StyledContainer>
    );
};
